<div class="flex flex-col h-full">
  
  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <div>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
        Pending Batches
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Faktury oczekujące na wysyłkę do KSeF
      </p>
    </div>
    <button 
      mat-flat-button 
      color="primary"
      (click)="refresh()"
      [disabled]="isLoading">
      <mat-icon [svgIcon]="'tabler_outline:refresh'"></mat-icon>
      Odśwież
    </button>
  </div>

  <!-- Summary Cards -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <mat-icon class="text-blue-600 dark:text-blue-400" [svgIcon]="'tabler_outline:file-invoice'"></mat-icon>
        <div>
          <div class="text-2xl font-bold text-blue-900 dark:text-blue-200">{{ totalPendingInvoices }}</div>
          <div class="text-sm text-blue-800 dark:text-blue-300">Faktur oczekujących</div>
        </div>
      </div>
    </div>
    
    <div class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <mat-icon class="text-purple-600 dark:text-purple-400" [svgIcon]="'tabler_outline:users'"></mat-icon>
        <div>
          <div class="text-2xl font-bold text-purple-900 dark:text-purple-200">{{ totalUsers }}</div>
          <div class="text-sm text-purple-800 dark:text-purple-300">Użytkowników w kolejce</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
    <mat-spinner [diameter]="60"></mat-spinner>
    <p class="text-gray-600 dark:text-gray-400 mt-4">Ładowanie pending batches...</p>
  </div>

  <!-- Table -->
  <div *ngIf="!isLoading" class="flex-1 overflow-hidden bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
    <div class="overflow-auto h-full">
      <table mat-table [dataSource]="dataSource" class="w-full">
        
        <!-- User Email Column -->
        <ng-container matColumnDef="userEmail">
          <th mat-header-cell *matHeaderCellDef>Użytkownik</th>
          <td mat-cell *matCellDef="let batch">
            <div class="text-sm font-medium text-gray-900 dark:text-white">
              {{ batch.userEmail }}
            </div>
          </td>
        </ng-container>

        <!-- Company Name Column -->
        <ng-container matColumnDef="companyName">
          <th mat-header-cell *matHeaderCellDef>Firma</th>
          <td mat-cell *matCellDef="let batch">
            <div class="text-sm text-gray-700 dark:text-gray-300">
              {{ batch.companyName || '-' }}
            </div>
          </td>
        </ng-container>

        <!-- Invoices Count Column -->
        <ng-container matColumnDef="invoicesCount">
          <th mat-header-cell *matHeaderCellDef class="text-center">Faktur</th>
          <td mat-cell *matCellDef="let batch" class="text-center">
            <mat-chip color="primary">{{ batch.invoicesCount }}</mat-chip>
          </td>
        </ng-container>

        <!-- Waiting Time Column -->
        <ng-container matColumnDef="waitingTime">
          <th mat-header-cell *matHeaderCellDef>Czas oczekiwania</th>
          <td mat-cell *matCellDef="let batch">
            <div class="flex items-center gap-2">
              <mat-icon 
                [svgIcon]="getAlertIcon(batch.alertLevel)"
                [ngClass]="{
                  'text-gray-500': batch.alertLevel === 'normal',
                  'text-yellow-500': batch.alertLevel === 'warning',
                  'text-red-500': batch.alertLevel === 'critical'
                }">
              </mat-icon>
              <span 
                class="text-sm font-medium"
                [ngClass]="{
                  'text-gray-700 dark:text-gray-300': batch.alertLevel === 'normal',
                  'text-yellow-700 dark:text-yellow-300': batch.alertLevel === 'warning',
                  'text-red-700 dark:text-red-300': batch.alertLevel === 'critical'
                }">
                {{ batch.waitingTime }}
              </span>
            </div>
          </td>
        </ng-container>

        <!-- Oldest Pending Column -->
        <ng-container matColumnDef="oldestPending">
          <th mat-header-cell *matHeaderCellDef>Najstarsza</th>
          <td mat-cell *matCellDef="let batch">
            <div class="text-sm text-gray-700 dark:text-gray-300">
              {{ batch.oldestPending | date:'dd.MM.yyyy HH:mm' }}
            </div>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="w-32">Akcje</th>
          <td mat-cell *matCellDef="let batch">
            <button 
              mat-icon-button 
              [matTooltip]="'Zobacz faktury'"
              color="primary">
              <mat-icon [svgIcon]="'tabler_outline:eye'"></mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr 
          mat-row 
          *matRowDef="let row; columns: displayedColumns;"
          class="hover:bg-gray-50 dark:hover:bg-gray-700">
        </tr>

        <!-- Empty State -->
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell text-center py-12" [attr.colspan]="displayedColumns.length">
            <div class="flex flex-col items-center">
              <mat-icon 
                class="text-gray-400 dark:text-gray-600 mb-4"
                [svgIcon]="'tabler_outline:check-circle'"
                style="width: 64px; height: 64px; font-size: 64px;">
              </mat-icon>
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Brak pending batches!
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                Wszystkie faktury zostały przetworzone
              </p>
            </div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>

